import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  message,
  Spin,
  Popconfirm,
  Switch,
  Tooltip,
} from 'antd';
import {
  KeyOutlined,
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { activationCodesAPI } from '../services/api';

const { Title, Text } = Typography;

const ActivationCodes = () => {
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, [pagination.page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [codesRes, statsRes] = await Promise.all([
        activationCodesAPI.getAll({ page: pagination.page, limit: pagination.limit }),
        activationCodesAPI.getStats()
      ]);

      if (codesRes.data.success) {
        setCodes(codesRes.data.data.codes);
        setPagination(prev => ({
          ...prev,
          total: codesRes.data.data.pagination.total
        }));
      }
      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
    } catch (error) {
      message.error('获取数据失败');
      console.error('Fetch activation codes error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 创建单个激活码
  const handleCreate = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      
      const data = {
        code: values.code || undefined,
        maxUses: values.maxUses || 1,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
        description: values.description || ''
      };

      await activationCodesAPI.create(data);
      message.success('激活码创建成功');
      setCreateModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.message || '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 批量创建激活码
  const handleBatchCreate = async () => {
    try {
      setSubmitting(true);
      const values = await batchForm.validateFields();
      
      const data = {
        count: values.count,
        maxUses: values.maxUses || 1,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
        description: values.description || ''
      };

      const result = await activationCodesAPI.createBatch(data);
      message.success(result.data.message);
      setBatchModalVisible(false);
      batchForm.resetFields();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.message || '批量创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 编辑激活码
  const handleEdit = (record) => {
    setSelectedCode(record);
    editForm.setFieldsValue({
      isActive: record.isActive,
      maxUses: record.maxUses,
      expiresAt: record.expiresAt ? dayjs(record.expiresAt) : null,
      description: record.description
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await editForm.validateFields();
      
      await activationCodesAPI.update(selectedCode.id, {
        isActive: values.isActive,
        maxUses: values.maxUses,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
        description: values.description
      });

      message.success('更新成功');
      setEditModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('更新失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 删除激活码
  const handleDelete = async (id) => {
    try {
      await activationCodesAPI.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 复制激活码（兼容 HTTP 环境）
  const handleCopy = (code) => {
    // 优先使用 clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(() => {
        message.success('激活码已复制到剪贴板');
      }).catch(() => {
        fallbackCopy(code);
      });
    } else {
      fallbackCopy(code);
    }
  };

  // 兼容方案：使用 textarea 复制
  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success('激活码已复制到剪贴板');
    } catch (err) {
      message.error('复制失败，请手动复制');
    }
    document.body.removeChild(textarea);
  };

  // 切换激活状态
  const handleToggleStatus = async (record) => {
    try {
      await activationCodesAPI.update(record.id, { isActive: !record.isActive });
      message.success(`激活码已${record.isActive ? '禁用' : '启用'}`);
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns = [
    {
      title: '激活码',
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <Space>
          <Text strong style={{ fontFamily: 'monospace', fontSize: 16, letterSpacing: 2 }}>
            {code}
          </Text>
          <Tooltip title="复制">
            <Button 
              type="text" 
              size="small" 
              icon={<CopyOutlined />} 
              onClick={() => handleCopy(code)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record) => {
        // 检查是否已用完
        const isUsedUp = record.maxUses !== null && record.currentUses >= record.maxUses;
        // 检查是否已过期
        const isExpired = record.expiresAt && new Date(record.expiresAt) < new Date();
        
        if (!record.isActive) {
          return (
            <Tag color="default" icon={<CloseCircleOutlined />}>
              已禁用
            </Tag>
          );
        }
        
        if (isUsedUp) {
          return (
            <Tag color="orange" icon={<CheckCircleOutlined />}>
              已用完
            </Tag>
          );
        }
        
        if (isExpired) {
          return (
            <Tag color="red" icon={<CloseCircleOutlined />}>
              已过期
            </Tag>
          );
        }
        
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            可使用
          </Tag>
        );
      },
    },
    {
      title: '使用次数',
      key: 'uses',
      render: (_, record) => {
        const isUsedUp = record.maxUses !== null && record.currentUses >= record.maxUses;
        return (
          <Text type={isUsedUp ? 'secondary' : undefined} style={isUsedUp ? { textDecoration: 'line-through' } : {}}>
            {record.currentUses} / {record.maxUses || '∞'}
          </Text>
        );
      },
    },
    {
      title: '过期时间',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date) => {
        if (!date) return <Text type="secondary">永不过期</Text>;
        const isExpired = new Date(date) < new Date();
        return (
          <Text type={isExpired ? 'danger' : 'secondary'}>
            {dayjs(date).format('YYYY-MM-DD HH:mm')}
            {isExpired && ' (已过期)'}
          </Text>
        );
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc) => desc || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Switch 
            size="small"
            checked={record.isActive}
            onChange={() => handleToggleStatus(record)}
          />
          <Tooltip title="编辑">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="确定删除此激活码吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && codes.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>
          <KeyOutlined style={{ marginRight: 8 }} />
          VIP 激活码管理
        </Title>
        <Space>
          <Button 
            type="default" 
            icon={<ThunderboltOutlined />} 
            onClick={() => {
              batchForm.setFieldsValue({ count: 10, maxUses: 1 });
              setBatchModalVisible(true);
            }}
          >
            批量生成
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({ maxUses: 1 });
              setCreateModalVisible(true);
            }}
          >
            创建激活码
          </Button>
        </Space>
      </div>

      {/* Statistics */}
      <Row gutter={16}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="激活码总数"
              value={stats.totalCodes || 0}
              prefix={<KeyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="可使用"
              value={codes.filter(c => c.isActive && (c.maxUses === null || c.currentUses < c.maxUses) && (!c.expiresAt || new Date(c.expiresAt) >= new Date())).length}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="已用完"
              value={codes.filter(c => c.maxUses !== null && c.currentUses >= c.maxUses).length}
              valueStyle={{ color: '#faad14' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总使用次数"
              value={stats.totalUses || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Codes Table */}
      <Card title="激活码列表">
        <Table
          columns={columns}
          dataSource={codes}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page) => setPagination(prev => ({ ...prev, page })),
            showTotal: (total) => `共 ${total} 条`
          }}
          locale={{ emptyText: '暂无激活码' }}
        />
      </Card>

      {/* Create Modal */}
      <Modal
        title="创建激活码"
        open={createModalVisible}
        onOk={handleCreate}
        onCancel={() => setCreateModalVisible(false)}
        okText="创建"
        cancelText="取消"
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical" initialValues={{ maxUses: 1 }}>
          <Form.Item 
            name="code" 
            label="激活码（留空自动生成）"
            rules={[
              { pattern: /^\d{6}$/, message: '激活码必须是6位数字' }
            ]}
          >
            <Input 
              placeholder="留空将自动生成6位数字激活码" 
              maxLength={6}
              style={{ fontFamily: 'monospace', letterSpacing: 4, fontSize: 18, textAlign: 'center' }}
            />
          </Form.Item>
          <Form.Item name="maxUses" label="最大使用次数" initialValue={1}>
            <InputNumber min={1} placeholder="默认1次" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="expiresAt" label="过期时间（留空为永不过期）">
            <DatePicker 
              showTime 
              style={{ width: '100%' }} 
              placeholder="选择过期时间"
            />
          </Form.Item>
          <Form.Item name="description" label="描述/备注">
            <Input.TextArea rows={2} placeholder="可选：添加备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Batch Create Modal */}
      <Modal
        title="批量生成激活码"
        open={batchModalVisible}
        onOk={handleBatchCreate}
        onCancel={() => setBatchModalVisible(false)}
        okText="生成"
        cancelText="取消"
        confirmLoading={submitting}
      >
        <Form form={batchForm} layout="vertical">
          <Form.Item 
            name="count" 
            label="生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber min={1} max={100} placeholder="最多100个" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="maxUses" label="每个激活码的最大使用次数" initialValue={1}>
            <InputNumber min={1} placeholder="默认1次" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="expiresAt" label="过期时间（留空为永不过期）">
            <DatePicker 
              showTime 
              style={{ width: '100%' }} 
              placeholder="选择过期时间"
            />
          </Form.Item>
          <Form.Item name="description" label="描述/备注">
            <Input.TextArea rows={2} placeholder="可选：添加备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={`编辑激活码 - ${selectedCode?.code}`}
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => setEditModalVisible(false)}
        okText="保存"
        cancelText="取消"
        confirmLoading={submitting}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="isActive" label="状态" valuePropName="checked">
            <Switch checkedChildren="有效" unCheckedChildren="禁用" />
          </Form.Item>
          <Form.Item name="maxUses" label="最大使用次数">
            <InputNumber min={1} placeholder="默认1次" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="expiresAt" label="过期时间">
            <DatePicker 
              showTime 
              style={{ width: '100%' }} 
              placeholder="选择过期时间"
            />
          </Form.Item>
          <Form.Item name="description" label="描述/备注">
            <Input.TextArea rows={2} placeholder="可选：添加备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default ActivationCodes;

